import type { PromoCode } from '@/api/promo-codes/promoQueries';
import { Button } from '@/components/common/Button';
import { DialogWrapper } from '@/components/common/DialogWrapper';
import { Input } from '@/components/common/Input';
import { RelationActionButton } from '@/components/common/RelationActionButton';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@/components/common/Table';
import { promoEmailSchema } from '@/validation/promoValidator';
import { useFormik } from 'formik';
import { useEffect } from 'react';
import { toFormikValidate } from 'zod-formik-adapter';

interface EditRecipientEmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  promoCode: PromoCode | null;
  onSubmit: (code: string, email: string) => void;
  onSend: (code: string, email: string) => void;
  isSendPending?: boolean;
}

export function EditRecipientEmailDialog({
  open,
  onOpenChange,
  promoCode,
  onSubmit,
  onSend,
  isSendPending = false,
}: EditRecipientEmailDialogProps) {
  const formik = useFormik({
    initialValues: { email: '' },
    validate: toFormikValidate(promoEmailSchema),
    onSubmit: (values) => {
      if (promoCode) {
        onSubmit(promoCode.code, values.email);
      }
    },
  });

  useEffect(() => {
    if (promoCode && open) {
      formik.resetForm({ values: { email: '' } });
    }
  }, [promoCode, open]);

  if (!promoCode) return null;

  const handleSend = async () => {
    const errors = await formik.validateForm();
    formik.setTouched({ email: true });

    if (Object.keys(errors).length === 0 && formik.values.email) {
      onSend(promoCode.code, formik.values.email);
    }
  };

  const isExpired = (expireDate: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expire = new Date(expireDate);
    return expire < today;
  };

  return (
    <DialogWrapper
      open={open}
      onOpenChange={onOpenChange}
      title="Assign Recipient"
      maxWidth="lg"
    >
      <form onSubmit={formik.handleSubmit} className="mt-4 space-y-6">
        <Table>
          <TableBody>
            <TableRow className="border-none hover:bg-transparent">
              <TableCell className="text-table-foreground w-24 px-0 py-2">
                Code:
              </TableCell>
              <TableCell className="text-table-foreground px-0 py-2">
                {promoCode.code}
              </TableCell>
            </TableRow>
            <TableRow className="border-none hover:bg-transparent">
              <TableCell className="text-table-foreground w-24 px-0 py-2">
                Discount:
              </TableCell>
              <TableCell className="text-table-foreground px-0 py-2">
                {promoCode.discount}%
              </TableCell>
            </TableRow>
            <TableRow className="border-none hover:bg-transparent">
              <TableCell className="text-table-foreground w-24 px-0 py-2">
                Valid till:
              </TableCell>
              <TableCell className="text-table-foreground px-0 py-2">
                {promoCode.expire_date}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        <div className="space-y-2">
          <Input
            id="recipient-email"
            name="email"
            label="Recipient Email"
            type="email"
            placeholder="Enter recipient email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.errors.email}
            touched={formik.touched.email}
            className="border-input h-15 w-full bg-white"
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex gap-4">
            {!promoCode.order_id && (
              <Button
                type="button"
                variant="primary"
                size="md"
                disabled={
                  isSendPending ||
                  !formik.values.email ||
                  (formik.touched.email && !!formik.errors.email) ||
                  isExpired(promoCode.expire_date)
                }
                onClick={handleSend}
              >
                {isSendPending ? 'Sending...' : 'Send'}
              </Button>
            )}
          </div>
          <RelationActionButton
            type="button"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </RelationActionButton>
        </div>
      </form>
    </DialogWrapper>
  );
}
